import { defineStore } from 'pinia';
import { invoiceService } from '@/services/invoiceService';
import { mollieService } from '@/services/mollieService';

const DEFAULT_FILTERS = {
  status: null,
  invoice_type: null,
  search: '',
  from_date: null,
  to_date: null,
};

const DEFAULT_PAGINATION = {
  total: 0,
  page: 1,
  limit: 50,
  total_pages: 0,
};

export const useInvoiceStore = defineStore('invoices', {
  state: () => ({
    invoices: [],
    invoicesLoading: false,
    pagination: { ...DEFAULT_PAGINATION },

    currentInvoice: null,
    currentInvoiceLoading: false,

    stats: null,
    statsLoading: false,

    settings: null,
    settingsLoading: false,
    settingsSaving: false,

    triggerAvailability: null,
    triggerAvailabilityLoaded: false,

    filters: { ...DEFAULT_FILTERS },

    error: null,
  }),

  getters: {
    hasModuleSlimInnen: (state) => {
      return !!state.triggerAvailability?.modules?.slim_innen;
    },
    hasModuleSlimWerken: (state) => {
      return !!state.triggerAvailability?.modules?.slim_werken;
    },

    // ── Mollie integration summary (from trigger-availability) ────────────
    // The detailed status (KYC, mode, profile name) lives in `mollieStore`.
    mollieIntegration: (state) => {
      return state.triggerAvailability?.integrations?.mollie || null;
    },
    mollieEligible: (state) => {
      return !!state.triggerAvailability?.integrations?.mollie?.eligible;
    },
    mollieConnected: (state) => {
      return !!state.triggerAvailability?.integrations?.mollie?.connected;
    },
    mollieReason: (state) => {
      return state.triggerAvailability?.integrations?.mollie?.reason || null;
    },

    // ── Invoicing capability flags (driven by Slim Innen + Mollie) ────────
    canMollieProcessPayments: (state) => {
      return !!state.triggerAvailability?.invoicing?.mollie_payment_processing;
    },
    canPublicPaymentPage: (state) => {
      return !!state.triggerAvailability?.invoicing?.public_payment_page;
    },
    canAutomaticPaymentSync: (state) => {
      return !!state.triggerAvailability?.invoicing?.automatic_payment_sync;
    },
  },

  actions: {
    // ============================================
    // FILTERS
    // ============================================

    setFilters(partial) {
      this.filters = { ...this.filters, ...partial };
    },

    clearFilters() {
      this.filters = { ...DEFAULT_FILTERS };
    },

    // ============================================
    // LIST
    // ============================================

    async fetchInvoices(params = {}, append = false) {
      this.invoicesLoading = true;
      this.error = null;
      try {
        const queryParams = {
          ...this.filters,
          ...params,
        };
        Object.keys(queryParams).forEach((key) => {
          if (queryParams[key] === null || queryParams[key] === '') {
            delete queryParams[key];
          }
        });

        const data = await invoiceService.getInvoices(queryParams);
        const list = data.invoices || [];
        this.invoices = append ? [...this.invoices, ...list] : list;
        this.pagination = data.pagination || DEFAULT_PAGINATION;
        return data;
      } catch (error) {
        this.error = error?.response?.data?.error || error.message;
        throw error;
      } finally {
        this.invoicesLoading = false;
      }
    },

    // ============================================
    // DETAIL
    // ============================================

    async fetchInvoice(id) {
      this.currentInvoiceLoading = true;
      this.error = null;
      try {
        const data = await invoiceService.getInvoiceById(id);
        const invoice = data.invoice || data;
        this.currentInvoice = invoice;
        return invoice;
      } catch (error) {
        this.error = error?.response?.data?.error || error.message;
        throw error;
      } finally {
        this.currentInvoiceLoading = false;
      }
    },

    clearCurrentInvoice() {
      this.currentInvoice = null;
    },

    // ============================================
    // STATS
    // ============================================

    async fetchStats(params = {}) {
      this.statsLoading = true;
      try {
        const data = await invoiceService.getStats(params);
        this.stats = data;
        return data;
      } catch (error) {
        this.error = error?.response?.data?.error || error.message;
        throw error;
      } finally {
        this.statsLoading = false;
      }
    },

    // ============================================
    // CREATE / MUTATIONS
    // ============================================

    async createInvoice(data) {
      const created = await invoiceService.createInvoice(data);
      const invoice = created.invoice || created;
      if (invoice?.id) {
        this.invoices = [invoice, ...this.invoices];
      }
      return invoice;
    },

    async getBillableItems(projectId) {
      const data = await invoiceService.getBillableItems(projectId);
      return data;
    },

    async mergeDraftInvoices(invoiceIds) {
      const result = await invoiceService.mergeDraftInvoices(invoiceIds);
      const merged = result.invoice || result;
      const removeIds = new Set(invoiceIds);
      this.invoices = this.invoices.filter((inv) => !removeIds.has(inv.id));
      if (merged?.id) {
        this.invoices = [merged, ...this.invoices];
      }
      return merged;
    },

    async bulkDeleteDrafts(invoiceIds) {
      const results = await Promise.allSettled(
        invoiceIds.map((id) => invoiceService.deleteDraft(id))
      );
      const succeededIds = new Set();
      results.forEach((res, idx) => {
        if (res.status === 'fulfilled') succeededIds.add(invoiceIds[idx]);
      });
      this.invoices = this.invoices.filter((i) => !succeededIds.has(i.id));
      return {
        succeeded: Array.from(succeededIds),
        failed: invoiceIds.filter((id) => !succeededIds.has(id)),
      };
    },

    async createDepositInvoice(projectId, body = {}) {
      const created = await invoiceService.createDepositInvoice(projectId, body);
      return created.invoice || created;
    },

    async createFinalInvoice(projectId, body = {}) {
      const created = await invoiceService.createFinalInvoice(projectId, body);
      return created.invoice || created;
    },

    async createFromProposal(proposalId, body = {}) {
      const created = await invoiceService.createFromProposal(proposalId, body);
      return created.invoice || created;
    },

    async createCreditNote(invoiceId, body) {
      const created = await invoiceService.createCreditNote(invoiceId, body);
      const note = created.invoice || created;
      if (this.currentInvoice?.id === invoiceId) {
        await this.fetchInvoice(invoiceId);
      }
      return note;
    },

    async updateInvoice(id, data) {
      const updated = await invoiceService.updateInvoice(id, data);
      const invoice = updated.invoice || updated;

      if (this.currentInvoice?.id === id) {
        this.currentInvoice = { ...this.currentInvoice, ...invoice };
      }
      const idx = this.invoices.findIndex((i) => i.id === id);
      if (idx !== -1) {
        this.invoices[idx] = { ...this.invoices[idx], ...invoice };
      }
      return invoice;
    },

    async deleteDraft(id) {
      await invoiceService.deleteDraft(id);
      this.invoices = this.invoices.filter((i) => i.id !== id);
      if (this.currentInvoice?.id === id) {
        this.currentInvoice = null;
      }
    },

    async sendInvoice(id) {
      const result = await invoiceService.sendInvoice(id);
      const invoice = result.invoice || result;
      if (this.currentInvoice?.id === id) {
        this.currentInvoice = { ...this.currentInvoice, ...invoice };
      }
      const idx = this.invoices.findIndex((i) => i.id === id);
      if (idx !== -1) {
        this.invoices[idx] = { ...this.invoices[idx], ...invoice };
      }
      return invoice;
    },

    /**
     * Send an invoice via email.
     *
     * Backend returns 202 with `{ invoice, job_id, queued_recipients }` and
     * the invoice flips to `status='sending'`. Use `pollInvoiceSendStatus(id)`
     * afterwards to wait for the worker to finish.
     *
     * For older deploys that still return 200 sync, the legacy branch is kept
     * so the UI does not break during a partial deploy.
     */
    async emailInvoice(id, body = {}) {
      const { status, data } = await invoiceService.emailInvoice(id, body);

      if (status === 202) {
        if (this.currentInvoice?.id === id && data?.invoice) {
          this.currentInvoice = { ...this.currentInvoice, ...data.invoice };
        }
        const idx = this.invoices.findIndex((inv) => inv.id === id);
        if (idx !== -1 && data?.invoice) {
          this.invoices[idx] = { ...this.invoices[idx], ...data.invoice };
        }
        return {
          mode: 'async',
          resent: !!data?.invoice?.resent,
          invoice: data?.invoice,
          jobId: data?.job_id,
          recipients: data?.queued_recipients || [],
          raw: data,
        };
      }

      if (this.currentInvoice?.id === id) {
        await this.fetchInvoice(id);
      }
      return { mode: 'sync', invoice: data, raw: data };
    },

    /**
     * Manually send a payment reminder for one invoice.
     */
    async remindInvoice(id, body = {}) {
      const data = await invoiceService.remindInvoice(id, body);
      const invoice = data?.invoice || data;
      if (this.currentInvoice?.id === id && invoice) {
        this.currentInvoice = { ...this.currentInvoice, ...invoice };
      }
      const idx = this.invoices.findIndex((inv) => inv.id === id);
      if (idx !== -1 && invoice) {
        this.invoices[idx] = { ...this.invoices[idx], ...invoice };
      }
      return data;
    },

    /**
     * Bulk send payment reminders. Updates local list for successful sends.
     */
    async remindInvoicesBulk(invoiceIds) {
      const result = await invoiceService.remindInvoicesBulk(invoiceIds);
      const succeeded = (result?.results || []).filter((r) => r.success);
      for (const row of succeeded) {
        const idx = this.invoices.findIndex((inv) => inv.id === row.id);
        if (idx !== -1) {
          this.invoices[idx] = {
            ...this.invoices[idx],
            reminders_sent: row.reminders_sent,
          };
        }
        if (this.currentInvoice?.id === row.id) {
          this.currentInvoice = {
            ...this.currentInvoice,
            reminders_sent: row.reminders_sent,
          };
        }
      }
      return result;
    },

    /**
     * Poll `GET /invoices/:id` until the worker has finished sending.
     *
     * Resolves with `{ result: 'sent'|'failed'|'timed_out', invoice }`.
     * - `sent`       → worker succeeded (`status='sent'`)
     * - `failed`     → worker reverted to `status='draft'`
     * - `timed_out`  → did not see a terminal state within `timeoutMs`
     *
     * @param {string} invoiceId
     * @param {Object} opts
     * @param {(invoice: any) => void} [opts.onUpdate]   called for every poll tick
     * @param {number} [opts.intervalMs=2000]            poll interval (default 2s)
     * @param {number} [opts.timeoutMs=60000]            give up after this long (default 60s)
     */
    async pollInvoiceSendStatus(invoiceId, { onUpdate, intervalMs = 2000, timeoutMs = 60000 } = {}) {
      const start = Date.now();
      while (true) {
        const fresh = await invoiceService.getInvoiceById(invoiceId);

        if (this.currentInvoice?.id === invoiceId) {
          this.currentInvoice = { ...this.currentInvoice, ...fresh };
        }
        const idx = this.invoices.findIndex((inv) => inv.id === invoiceId);
        if (idx !== -1) {
          this.invoices[idx] = { ...this.invoices[idx], ...fresh };
        }

        if (typeof onUpdate === 'function') onUpdate(fresh);

        if (fresh.status === 'sent') {
          return { result: 'sent', invoice: fresh };
        }
        if (fresh.status === 'draft') {
          return { result: 'failed', invoice: fresh };
        }

        if (Date.now() - start > timeoutMs) {
          return { result: 'timed_out', invoice: fresh };
        }

        await new Promise((resolve) => setTimeout(resolve, intervalMs));
      }
    },

    // ============================================
    // PAYMENTS
    // ============================================

    async recordPayment(id, body) {
      const result = await invoiceService.recordPayment(id, body);
      if (this.currentInvoice?.id === id) {
        await this.fetchInvoice(id);
      }
      return result;
    },

    async deletePayment(id, paymentId) {
      const result = await invoiceService.deletePayment(id, paymentId);
      if (this.currentInvoice?.id === id) {
        await this.fetchInvoice(id);
      }
      return result;
    },

    /**
     * Generate a Mollie checkout URL + QR code for an existing invoice.
     * Used by the "Genereer betaallink" action on the invoice detail page.
     * Errors propagate up so the modal can display an HTTP-error toast.
     */
    async createPaymentLink(invoiceId, body = {}) {
      return mollieService.createPaymentLink(invoiceId, body);
    },

    // ============================================
    // SETTINGS
    // ============================================

    async fetchSettings(force = false) {
      if (this.settings && !force) return this.settings;
      this.settingsLoading = true;
      try {
        const data = await invoiceService.getSettings();
        this.settings = data;
        return data;
      } catch (error) {
        this.error = error?.response?.data?.error || error.message;
        throw error;
      } finally {
        this.settingsLoading = false;
      }
    },

    async updateSettings(data) {
      this.settingsSaving = true;
      try {
        const updated = await invoiceService.updateSettings(data);
        this.settings = { ...(this.settings || {}), ...updated };
        return updated;
      } catch (error) {
        this.error = error?.response?.data?.error || error.message;
        throw error;
      } finally {
        this.settingsSaving = false;
      }
    },

    // ============================================
    // TRIGGER AVAILABILITY
    // ============================================

    async fetchTriggerAvailability(force = false) {
      if (this.triggerAvailabilityLoaded && !force) return this.triggerAvailability;
      try {
        const data = await invoiceService.getTriggerAvailability();
        this.triggerAvailability = data;
        this.triggerAvailabilityLoaded = true;
        return data;
      } catch (error) {
        console.warn('Failed to load trigger availability', error);
        return null;
      }
    },

    // ============================================
    // RESET
    // ============================================

    $reset() {
      this.invoices = [];
      this.invoicesLoading = false;
      this.pagination = { ...DEFAULT_PAGINATION };
      this.currentInvoice = null;
      this.currentInvoiceLoading = false;
      this.stats = null;
      this.statsLoading = false;
      this.settings = null;
      this.settingsLoading = false;
      this.settingsSaving = false;
      this.triggerAvailability = null;
      this.triggerAvailabilityLoaded = false;
      this.filters = { ...DEFAULT_FILTERS };
      this.error = null;
    },
  },
});

export default useInvoiceStore;
